import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/AuthContext';
import { FAST_TRACK_WEEKS } from '@/types/user';

/**
 * Syncs in-memory user progression state with the member_progress table.
 * - On mount (when user exists), loads from DB
 * - On user state changes, debounce-saves to DB
 */
export function useMemberProgress() {
  const { user, updateUser, session } = useAuth();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loaded = useRef(false);
  const savedPhase = useRef<number | null>(null);

  // Load progress from DB on login
  useEffect(() => {
    if (!session?.user || loaded.current) return;

    const load = async () => {
      const uid = session.user.id;
      const { data } = await supabase
        .from('member_progress')
        .select('*')
        .eq('user_id', uid)
        .maybeSingle();

      if (data) {
        savedPhase.current = data.current_phase as number;
        const defaultWeeks = FAST_TRACK_WEEKS.map(w => ({ ...w, completed: false, homeworkSubmitted: false }));
        const dbWeeks = data.fast_track_weeks as Record<string, unknown>[] | null;
        updateUser({
          phase: data.current_phase as number,
          phase1Complete: (data.phase1_complete as boolean[]) ?? [false, false, false],
          fastTrackWeeks: (Array.isArray(dbWeeks) && dbWeeks.length >= 5) ? dbWeeks : defaultWeeks,
          declaration: data.declaration ?? '',
          declarationSubmitted: data.declaration_submitted ?? false,
          criteriaSubmitted: data.criteria_submitted ?? false,
          clarityCallBooked: data.clarity_call_booked ?? false,
          criteriaCards: (data.criteria_cards as { label: string; value: string }[]) ?? null,
        });
      }
      loaded.current = true;
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Save progress to DB on changes (debounced)
  const saveProgress = useCallback(() => {
    if (!user || !session?.user) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const uid = session.user.id;
      const previousPhase = savedPhase.current;
      const newPhase = user.phase;
      // Never let the DB phase go backward — navigating back to review a prior
      // phase shouldn't overwrite the highest phase the user has reached.
      const phaseToSave = Math.max(newPhase, previousPhase ?? newPhase);
      const payload = {
        user_id: uid,
        current_phase: phaseToSave,
        phase1_complete: user.phase1Complete,
        fast_track_weeks: user.fastTrackWeeks,
        criteria_cards: user.criteriaCards ?? null,
        declaration: user.declaration,
        declaration_submitted: user.declarationSubmitted,
        criteria_submitted: user.criteriaSubmitted,
        clarity_call_booked: user.clarityCallBooked,
      };

      await supabase
        .from('member_progress')
        .upsert(payload, { onConflict: 'user_id' });

      if (previousPhase !== null && phaseToSave > previousPhase) {
        supabase.from('member_events').insert({
          user_id: uid,
          event_type: 'member.phase_entered',
          properties: { phase: phaseToSave, previous_phase: previousPhase },
        });
      }
      savedPhase.current = phaseToSave;
    }, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session?.user?.id]);

  // Trigger save whenever user state changes
  useEffect(() => {
    if (loaded.current && user) {
      saveProgress();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.phase,
    user?.phase1Complete,
    user?.fastTrackWeeks,
    user?.declaration,
    user?.declarationSubmitted,
    user?.criteriaSubmitted,
    user?.clarityCallBooked,
    user?.criteriaCards,
    saveProgress,
  ]);

  // Track content view
  const trackContentView = useCallback(async (contentId: string) => {
    if (!session?.user) return;
    await supabase
      .from('content_progress')
      .upsert(
        { user_id: session.user.id, content_id: contentId },
        { onConflict: 'user_id,content_id' }
      );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  return { trackContentView };
}
